import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import apiConfig from 'constants/apiConfig';
import { incidentRosterKeys } from 'components/IAP/hooks/incidentRoster/incidentRoster.queryKeys';
import { useFetchIncidentWorkspaceTeamStatus } from 'hooks/useFetchIncidentWorkspaceTeamStatus';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router';
import { addMemberToTeams } from 'actions/teamsActions';

interface AssignExternalUserParams {
  incidentId: string;
  positionId: string;
  positionName: string;
  groupId: string;
  isLeader: boolean;
  externalUser: {
    emailAddress: string;
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    organization: {
      id: string;
      name: string;
    };
  };
}

export const useAssignExternalUser = () => {
  const queryClient = useQueryClient();
  const { data: teamsStatus } = useFetchIncidentWorkspaceTeamStatus();
  const [searchParams] = useSearchParams();
  const isDraft = searchParams.get('mode') === 'draft';
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (params: AssignExternalUserParams) => {
      const response = await axios(
        apiConfig.apiGateway.assignExternalUser(params.incidentId),
        {
          method: 'POST',
          data: {
            groupId: params.groupId,
            positionId: params.positionId,
            positionName: params.positionName,
            isLeader: params.isLeader,
            externalUser: params.externalUser,
            isDraft,
          },
        }
      );
      return response.data;
    },
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['externalOrganizations'],
      });
      queryClient.invalidateQueries({
        queryKey: ['incidentRoster', variables.incidentId],
      });
      queryClient.invalidateQueries({
        queryKey: incidentRosterKeys.draftByIncident(variables.incidentId),
      });

      if (teamsStatus === 'done') {
        dispatch(
          addMemberToTeams(variables.incidentId, response.newUser.userGuid)
        );
      }

      toast.success('Successfully assigned external user to incident');
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.ERROR ||
          'Failed to assign external user to incident'
      );
    },
  });
};
